/* ═══════════════════════════════════════════════════════════
   SHARED HELPERS  (Template 1 + Template 2 banne vapre)
   File: invoiceShared.jsx  (Invoice.jsx ni bajuma j rakhvi)
═══════════════════════════════════════════════════════════ */

/* seller details — change as you need */
export const SELLER = {
  name: 'KAILASBEN JIGNESHBHAI GHOGHARI',
  lines: ['PLOT NO 57, BHAYCHAND NAGAR SOCIETY,', 'KATARGAM ROAD, Surat', 'Surat, GUJARAT, 395004', 'IN'],
  pan: 'EPIPG6078B',
  gst: '24EPIPG6078B1Z0',
  stateCode: '24',
  details: 'GJ-574785363-2627',
}

export const r2 = n => Math.round((n + Number.EPSILON) * 100) / 100
export const inr = n => '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Computes net / tax / total for every row + grand totals */
export function calc(inv) {
  const rows = inv.items.map(it => {
    const net = r2(it.unitPrice * it.qty - (it.discount || 0) * it.qty)
    const total = it.total ?? r2(net * (1 + it.taxRate / 100))
    return { ...it, net, total, tax: r2(total - net) }
  })
  const sum = k => r2(rows.reduce((a, r) => a + r[k], 0))
  return { rows, net: sum('net'), tax: sum('tax'), total: sum('total'), hasDiscount: rows.some(r => r.discount > 0) }
}

/** Indian-style amount in words (Amazon format: "... Point Nine only") */
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
const below100 = n => (n < 20 ? ONES[n] : TENS[Math.floor(n / 10)] + (n % 10 ? '-' + ONES[n % 10].toLowerCase() : ''))
const below1000 = n => (n >= 100 ? ONES[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' : '') : '') + below100(n % 100)
export function inWords(num) {
  let rem = Math.floor(num)
  const frac = Math.round((num - rem) * 100)
  let w = ''
  for (const [v, label] of [[10000000, 'Crore'], [100000, 'Lakh'], [1000, 'Thousand']]) {
    if (rem >= v) {
      const q = Math.floor(rem / v)
      w += (q >= 100 ? below1000(q) : below100(q)) + ' ' + label + ' '
      rem %= v
    }
  }
  w += below1000(rem)
  if (!w.trim()) w = 'Zero'
  if (frac) {
    const s = String(frac).padStart(2, '0').replace(/0+$/, '')
    w += ' Point ' + [...s].map(d => (d === '0' ? 'Zero' : ONES[+d])).join(' ')
  }
  return w.trim() + ' only'
}

/* ───────────── Code 128 barcode ───────────── */
const C128 = '212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 122231 113222 123122 123221 223211 221132 221231 213212 223112 312131 311222 321122 321221 312212 322112 322211 212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 231113 231311 112133 112331 132131 113123 113321 133121 313121 211331 231131 213113 213311 213131 311123 311321 331121 312113 312311 332111 314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 111242 121142 121241 114212 124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 114311 411113 411311 113141 114131 311141 411131 211412 211214 211232 2331112'.split(' ')

function encode(v) {
  let x = 0
  const bars = []
  v.forEach(q => {
    let bar = true
    for (const ch of C128[q]) { const w = +ch; if (bar) bars.push([x, w]); x += w; bar = !bar }
  })
  return { bars, total: x }
}

/** Code 128-C (only even-length numeric text) */
export function code128C(digits) {
  const v = [105]
  for (let i = 0; i < digits.length; i += 2) v.push(+digits.slice(i, i + 2))
  let sum = 105
  for (let i = 1; i < v.length; i++) sum += v[i] * i
  v.push(sum % 103, 106)
  return encode(v)
}

/** Code 128 — numeric (even length) => C, baki badhu (letters + digits) => B */
export function code128(text) {
  const s = String(text ?? '')
  if (/^\d+$/.test(s) && s.length % 2 === 0) return code128C(s)
  const v = [104]
  for (const ch of s) v.push(Math.min(95, Math.max(0, ch.charCodeAt(0) - 32)))
  let sum = 104
  for (let i = 1; i < v.length; i++) sum += v[i] * i
  v.push(sum % 103, 106)
  return encode(v)
}

/* decorative placeholder QR (replace with a real QR lib if needed) */
export const QrMock = ({ seed }) => {
  const n = 21
  let h = 7
  for (const c of String(seed ?? '')) h = (h * 31 + c.charCodeAt(0)) >>> 0
  const rects = []
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      let on
      if ((x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7)) {
        const lx = x < 7 ? x : x - (n - 7), ly = y < 7 ? y : y - (n - 7)
        on = lx === 0 || lx === 6 || ly === 0 || ly === 6 || (lx >= 2 && lx <= 4 && ly >= 2 && ly <= 4)
      } else {
        h = (h * 1103515245 + 12345) >>> 0
        on = (h >>> 16) & 1
      }
      if (on) rects.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" />)
    }
  }
  return <svg className="ai-qr" viewBox={`0 0 ${n} ${n}`} fill="currentColor" shapeRendering="crispEdges">{rects}</svg>
}

/* decorative placeholder DataMatrix (standalone svg) — swap with bwip-js for real codes */
export const DMatrixMock = ({ seed }) => {
  const n = 18
  let h = 11
  for (const ch of String(seed ?? '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  const cells = []
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
    let on
    if (i === 0 || j === n - 1) on = true
    else if (j === 0) on = i % 2 === 0
    else if (i === n - 1) on = j % 2 === 1
    else { h = (h * 1103515245 + 12345) >>> 0; on = ((h >>> 16) & 1) === 1 }
    if (on) cells.push(<rect key={`${i}-${j}`} x={i} y={j} width="1.04" height="1.04" />)
  }
  return <svg className="sd-dm-svg" viewBox={`0 0 ${n} ${n}`} fill="currentColor" shapeRendering="crispEdges" aria-hidden="true">{cells}</svg>
}